/**
 * Cart Upsell Variant Select Component
 * Handles variant selection for cart upsell products
 */
class CartUpsellVariantSelect extends HTMLElement {
  constructor() {
    super();
    this.variantData = null;
    this.selects = null;
    this.form = null;
    this.variantInput = null;
    this.submitButton = null;
    this.priceContainer = null;
    this.productCard = null;
    this.boundOnVariantChange = this.onVariantChange.bind(this);
  }

  connectedCallback() {
    this.selects = this.querySelectorAll('select');
    this.form = this.closest('form');
    this.productCard = this.closest('[data-product-card]');
    this.variantInput = this.form?.querySelector('input[name="id"]');
    this.submitButton = this.form?.querySelector('button[type="submit"]');
    this.priceContainer = this.productCard?.querySelector('.price-block');
    this.sizeOptionPosition = parseInt(this.dataset.sizeOptionPosition, 10) || 0;
    
    const variantJsonEl = this.querySelector('[data-variant-json]');
    if (variantJsonEl) {
      try {
        this.variantData = JSON.parse(variantJsonEl.textContent);
      } catch (e) {
        console.error('Error parsing variant JSON:', e);
        return;
      }
    }

    const packLabelsEl = this.querySelector('[data-pack-labels-json]');
    if (packLabelsEl) {
      try {
        this.packLabelsData = JSON.parse(packLabelsEl.textContent);
      } catch (e) {
        console.error('Error parsing pack labels JSON:', e);
      }
    }

    this.selects.forEach(select => {
      select.addEventListener('change', this.boundOnVariantChange);
    });

    this.updateSizePackLabels();
  }

  disconnectedCallback() {
    this.selects?.forEach(select => {
      select.removeEventListener('change', this.boundOnVariantChange);
    });
  }

  onVariantChange(e) {
    // Stop event from bubbling to prevent cart form from catching it
    e.stopPropagation();
    
    const selectedOptions = Array.from(this.selects).map(select => select.value);
    const matchedVariant = this.findVariant(selectedOptions);
    
    if (matchedVariant && this.variantInput) {
      this.variantInput.value = matchedVariant.id;
      this.updateUI(matchedVariant);
    } else {
      this.updateUI(null);
    }

    this.updateSizePackLabels();
  }

  getSelectedOptionsByIndex() {
    return Array.from(this.selects).map((select) => select.value);
  }

  findVariantForOptionValue(optionIndex, optionValue) {
    if (!this.variantData) return null;

    const targetIndex = optionIndex - 1;
    const selectedValues = this.getSelectedOptionsByIndex();

    const preferred = this.variantData.find((variant) => {
      if (variant.options[targetIndex] !== optionValue) return false;
      return variant.options.every((opt, i) => {
        if (i === targetIndex) return true;
        return !selectedValues[i] || selectedValues[i] === opt;
      });
    });

    return preferred || this.variantData.find((variant) => variant.options[targetIndex] === optionValue) || null;
  }

  updateSizePackLabels() {
    if (typeof PackLabel === 'undefined' || !this.packLabelsData || !this.sizeOptionPosition) return;

    const sizeSelect = this.querySelector('[data-size-select]');
    if (!sizeSelect) return;

    const optionIndex = parseInt(sizeSelect.dataset.optionIndex, 10);

    sizeSelect.querySelectorAll('[data-option-base-value]').forEach((option) => {
      const baseValue = option.dataset.optionBaseValue;
      const variant = this.findVariantForOptionValue(optionIndex, baseValue);
      const packValue = variant ? this.packLabelsData[variant.id] : null;
      const fullLabel = PackLabel.formatOptionLabel(baseValue, packValue);

      option.textContent = fullLabel;
    });

    if (sizeSelect.value) {
      const variant = this.findVariantForOptionValue(optionIndex, sizeSelect.value);
      const packValue = variant ? this.packLabelsData[variant.id] : null;
      const fullLabel = PackLabel.formatOptionLabel(sizeSelect.value, packValue);
      const optionName = sizeSelect.getAttribute('aria-label')?.split(':')[0]?.trim() || 'Size';
      sizeSelect.setAttribute('aria-label', `${optionName}: ${fullLabel}`);
    }
  }

  findVariant(selectedOptions) {
    if (!this.variantData) return null;
    
    return this.variantData.find(variant => {
      return variant.options.every((option, index) => option === selectedOptions[index]);
    });
  }

  updateUI(variant) {
    this.updateButtonState(variant);
    this.updatePrice(variant);
  }

  updateButtonState(variant) {
    if (!this.submitButton) return;
    
    const addText = this.submitButton.querySelector('.add-text');
    const isAvailable = variant?.available;
    
    this.submitButton.disabled = !isAvailable;
    
    if (addText) {
      addText.textContent = isAvailable 
        ? (globalThis.variantStrings?.add || 'Add')
        : (globalThis.variantStrings?.sold || 'Sold');
    }
  }

  updatePrice(variant) {
    if (!this.priceContainer || !variant) return;
    
    const priceEl = this.priceContainer.querySelector('[data-current-price]');
    const comparePriceEl = this.priceContainer.querySelector('[data-compare-price]');
    
    if (priceEl && typeof Utility !== 'undefined' && Utility.formatMoney) {
      priceEl.textContent = Utility.formatMoney(variant.price);
      priceEl.classList.toggle('discounted-price', variant.compare_at_price > variant.price);
    }
    
    if (comparePriceEl) {
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        comparePriceEl.innerHTML = `<del class="money">${Utility.formatMoney(variant.compare_at_price)}</del>`;
        comparePriceEl.classList.remove('d-none');
      } else {
        comparePriceEl.classList.add('d-none');
      }
    }
  }
}

customElements.define('cart-upsell-variant-select', CartUpsellVariantSelect);

/**
 * Cart Upsell Component
 * 
 * This component handles cart upsell functionality by:
 * - Listening to cart update events
 * - Updating upsell block HTML based on cart changes
 */

class CartUpsell extends HTMLElement {
  constructor() {
    super();
    
    this.upsellContainer = this.querySelector('[data-cart-upsell]');
    this.isUpdating = false;
    
    this.init();
  }

  /**
   * Initialize the component
   */
  init() {
    if (!this.upsellContainer) return;
    
    // Subscribe to cart update events
    this.unsubscribeCartUpdate = eventBus.subscribe(THEME_EVENTS.cartUpdate, this.handleCartUpdate.bind(this));
  }

  /**
   * Handle cart update events
   * @param {Object} eventData - Cart update event data
   */
  handleCartUpdate(eventData) {
    if (this.isUpdating) return;
    
    this.isUpdating = true;
    
    try {
      const { action, section } = eventData.data;
      
      // Update upsell block HTML if needed
      if(action == 'remove-item' || action == 'add-item') this.updateUpsellHTML(section);
    } catch (error) {
      console.error('Error handling in upsell component:', error);
    } finally {
      this.isUpdating = false;
    }
  }

  /**
   * Update upsell HTML with new content
   * @param {string} newHTML - New HTML content
   */
  updateUpsellHTML(newHTML) {
    if (!newHTML || !this.upsellContainer) return;
    
    const parsedHTML = ((typeof newHTML == 'string') ? Utility.DOM.parse(newHTML) : newHTML);
    const newUpsellContainer = parsedHTML.querySelector('[data-cart-upsell]');
    if (!newUpsellContainer) return;

    // Update the container content
    this.classList.remove('d-none');
    this.upsellContainer.innerHTML = newUpsellContainer.innerHTML;
    lazyImageObserver.observe();

    // Trigger swiper load if swiper-container is present in the loaded content
    // This ensures swiper bundle loads even if it wasn't present on initial page load
    if (typeof window.triggerSwiperLoad === 'function') {
      window.triggerSwiperLoad();
    }
  }

  /**
   * Clean up event listeners
   */
  disconnectedCallback() {
    if (this.unsubscribeCartUpdate) {
      this.unsubscribeCartUpdate();
    }
  }
}

// Register the custom element
customElements.define('cart-upsell', CartUpsell);
